import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { bookingsApi } from '../services/api';
import { PaymentButtons } from './PaymentButtons';


interface Payment {
  id: string;
  amount: number;
  tranche: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paidAt?: string;
  // Discount code fields
  importoOriginale?: number | null;
  importoFinale?: number | null;
  percentualeScontoApplicata?: number | null;
}

interface Trip {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
}

interface Booking {
  id: string;
  trip: Trip;
  totalAmount: number;
  numberOfQuotes: number;
  status: string;
  createdAt: string;
  payments: Payment[];
  participants?: Array<{ firstName: string; lastName: string; age?: number }>;
}

export const UserBookings: React.FC = () => {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingsApi.getUserBookings();
      setBookings(response.bookings || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(t('payment.loadingBookingsError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchBookings();
  };

  // Funzione per formattare la valuta
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('payment.yourBookings')}</h2>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('payment.yourBookings')}</h2>
        <div className="text-center py-8">
          <div className="text-red-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('payment.errorLoadingBookings')}</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('payment.yourBookings')}</h2>
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('payment.noBookings')}</h3>
          <p className="text-gray-600">{t('payment.noBookingsMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('payment.yourBookings')}</h2>
        <div className="space-y-6">
          {bookings.map((booking) => {
            const depositPayment = booking.payments.find(p => p.tranche === 1);
            const balancePayment = booking.payments.find(p => p.tranche === 2);
            
            const depositAmount = depositPayment?.amount || 0;
            
            // Calculate balance considering discount code
            // If balance payment already exists, use its importoFinale (discounted balance amount)
            // Otherwise, calculate: (discounted total - deposit)
            // If discount was applied, use importoFinale (discounted total) from deposit payment
            // Otherwise use booking.totalAmount (original total)
            const balanceAmount = balancePayment?.importoFinale 
              ? balancePayment.importoFinale 
              : (depositPayment?.importoFinale || booking.totalAmount) - depositAmount;
            
            // Total amount with discount (for display)
            const totalAmountWithDiscount = depositPayment?.importoFinale || booking.totalAmount;
            
            // Calculate discount details
            const originalAmount = depositPayment?.importoOriginale || booking.totalAmount;
            const hasDiscount = depositPayment?.importoFinale && depositPayment?.importoOriginale && 
                                depositPayment.importoFinale !== depositPayment.importoOriginale;
            const discountAmount = hasDiscount 
              ? ((depositPayment?.importoOriginale ?? 0) - (depositPayment?.importoFinale ?? 0))
              : 0;
            const discountPercentage = depositPayment?.percentualeScontoApplicata || 0;

            const paymentStatus = {
              deposit: depositPayment?.status || 'not_paid',
              balance: balancePayment?.status || 'not_paid'
            };

            const formatDate = (dateString: string) => {
              return new Date(dateString).toLocaleDateString('it-IT', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            };

            return (
              <div key={booking.id} className="border border-gray-200 rounded-lg p-6">
                {/* Trip Info */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{booking.trip.name}</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">{t('payment.tripDate')}:</span>
                      {booking.trip.startDate ? (
                        <span> {formatDate(booking.trip.startDate)}</span>
                      ) : (
                        <span> {t('payment.toBeConfirmed')}</span>
                      )}
                    </div>
                    <div>
                      <span className="font-medium">{t('payment.bookedOn')}:</span>
                      <span> {formatDate(booking.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Participants */}
                {booking.participants && booking.participants.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">{t('booking.participants')}</h4>
                    <div className="space-y-2">
                      {booking.participants.map((p, idx) => (
                        <div key={`${booking.id}-participant-${idx}`} className="text-sm text-gray-700">
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <div>
                              <span className="font-medium text-gray-900">{t('booking.firstName')}:</span>
                              <span className="ml-2">{p.firstName}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">{t('booking.lastName')}:</span>
                              <span className="ml-2">{p.lastName}</span>
                            </div>
                            {typeof p.age === 'number' && p.age > 0 && (
                              <div>
                                <span className="font-medium text-gray-900">{t('booking.age')}:</span>
                                <span className="ml-2">{p.age}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Summary - Detailed Breakdown */}
                <div className="mb-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Summary</h4>
                  <div className="space-y-2 text-sm">
                    {/* Original Price */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Original price:</span>
                      <span className="text-gray-900 font-medium">{formatCurrency(originalAmount)}</span>
                    </div>
                    
                    {/* Discount (if applied) */}
                    {hasDiscount && discountAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">
                          Discount {discountPercentage > 0 && `(${discountPercentage}%)`}:
                        </span>
                        <span className="text-green-600 font-medium">-{formatCurrency(discountAmount)}</span>
                      </div>
                    )}
                    
                    {/* Divider */}
                    <div className="border-t border-gray-300 my-2"></div>
                    
                    {/* Total Amount */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">Total amount:</span>
                      <span className="text-green-600 font-semibold">{formatCurrency(totalAmountWithDiscount)}</span>
                    </div>
                    {hasDiscount && (
                      <div className="text-xs text-gray-500 ml-auto text-right">
                        {formatCurrency(originalAmount)} - {formatCurrency(discountAmount)} ({discountPercentage}%) = {formatCurrency(totalAmountWithDiscount)}
                      </div>
                    )}
                    
                    {/* Deposit */}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-gray-700 font-medium">Deposit:</span>
                      <span className="text-green-600 font-semibold">{formatCurrency(depositAmount)}</span>
                    </div>
                    {booking.numberOfQuotes > 0 && depositAmount > 0 && (
                      <div className="text-xs text-gray-500 ml-auto text-right">
                        {formatCurrency(depositAmount / booking.numberOfQuotes)} × {booking.numberOfQuotes} = {formatCurrency(depositAmount)}
                      </div>
                    )}
                    
                    {/* Balance */}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-gray-700 font-medium">Balance:</span>
                      <span className="text-green-600 font-semibold">{formatCurrency(balanceAmount)}</span>
                    </div>
                    <div className="text-xs text-gray-500 ml-auto text-right">
                      {formatCurrency(totalAmountWithDiscount)} - {formatCurrency(depositAmount)} = {formatCurrency(balanceAmount)}
                    </div>
                    
                    {/* Discount Code Status */}
                    <div className="mt-3 pt-2 border-t border-gray-300">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Discount code:</span>
                        <span className={`font-medium ${hasDiscount ? 'text-green-600' : 'text-gray-500'}`}>
                          {hasDiscount ? 'Applied' : 'Not applied'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Status */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">{t('payment.paymentStatus')}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-gray-600">{t('payment.deposit')}: €{depositAmount}</span>
                      </div>
                      <span className={`text-sm px-2 py-1 rounded ${
                        paymentStatus.deposit === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : paymentStatus.deposit === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {paymentStatus.deposit === 'paid' ? t('payment.depositPaid') : 
                         paymentStatus.deposit === 'pending' ? t('payment.depositPending') : t('payment.notPaid')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-gray-600">{t('payment.balance')}: €{balanceAmount}</span>
                      </div>
                      <span className={`text-sm px-2 py-1 rounded ${
                        paymentStatus.balance === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : paymentStatus.balance === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {paymentStatus.balance === 'paid' ? t('payment.balancePaid') : 
                         paymentStatus.balance === 'pending' ? t('payment.balancePending') : t('payment.notPaid')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-900">{t('payment.total')}</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(totalAmountWithDiscount)}
                    </span>
                  </div>
                  {depositPayment?.importoOriginale && depositPayment?.importoFinale && depositPayment.importoOriginale !== depositPayment.importoFinale && (
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="line-through mr-2">{formatCurrency(depositPayment.importoOriginale)}</span>
                      {depositPayment.percentualeScontoApplicata && (
                        <span className="text-green-600">
                          -{depositPayment.percentualeScontoApplicata}%
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Buttons */}
                <div className="border-t pt-4 mt-4">
                  <PaymentButtons
                    bookingId={booking.id}
                    tripId={booking.trip.id}
                    tripName={booking.trip.name}
                    totalAmount={booking.totalAmount}
                    firstTrancheAmount={depositAmount}
                    secondTrancheAmount={balanceAmount}
                    payments={booking.payments}
                    onPaymentSuccess={handlePaymentSuccess}
                    className="mt-4"
                  />
                </div>

              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}; 